import { useState, useEffect } from 'react';
import { useRiderStore } from '@/store/rider';
import { earningsApi } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { logger } from '@/lib/logger';

type Period = 'day' | 'week' | 'month';

interface EarningsSummary {
  totalEarnings: number;
  totalDeliveries: number;
  averagePerDelivery: number;
  breakdown: Array<{
    date: string;
    earnings: number;
    deliveries: number;
  }>;
}

export default function EarningsPage() {
  const [period, setPeriod] = useState<Period>('day');
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const stats = useRiderStore((state) => state.stats);

  useEffect(() => {
    loadEarnings();
  }, [period]);

  const loadEarnings = async () => {
    try {
      setIsLoading(true);
      const data = await earningsApi.getSummary(period);
      setSummary(data);
      logger.info('Earnings loaded', { period, totalEarnings: data.totalEarnings });
    } catch (error) {
      logger.error('Failed to load earnings', error as Error, { period });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="bg-card border-b px-4 py-3">
        <h1 className="text-xl font-semibold">Earnings</h1>
      </header>

      <main className="p-4 space-y-4">
        {/* Period Selector */}
        <div className="flex gap-2">
          {(['day', 'week', 'month'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                period === p
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              {p === 'day' && 'Today'}
              {p === 'week' && 'This Week'}
              {p === 'month' && 'This Month'}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-1">Total Earnings</div>
                <div className="text-2xl font-bold text-primary">
                  {formatCurrency(summary?.totalEarnings || stats.todayEarnings)}
                </div>
              </div>

              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-1">Deliveries</div>
                <div className="text-2xl font-bold">
                  {summary?.totalDeliveries || stats.todayDeliveries}
                </div>
              </div>

              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-1">Average per Delivery</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(summary?.averagePerDelivery || 0)}
                </div>
              </div>

              <div className="bg-card border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-1">Rating</div>
                <div className="text-lg font-semibold flex items-center gap-1">
                  ⭐ {stats.rating.toFixed(1)}
                </div>
              </div>
            </div>

            {/* Breakdown */}
            {summary?.breakdown && summary.breakdown.length > 0 && (
              <div className="bg-card border rounded-lg p-4">
                <h2 className="font-semibold mb-4">Breakdown</h2>
                <div className="space-y-3">
                  {summary.breakdown.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <div className="font-medium">{item.date}</div>
                        <div className="text-sm text-muted-foreground">
                          {item.deliveries} {item.deliveries === 1 ? 'delivery' : 'deliveries'}
                        </div>
                      </div>
                      <div className="text-lg font-semibold text-primary">
                        {formatCurrency(item.earnings)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All-time Stats */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4">
              <h2 className="font-semibold mb-3">All-Time Stats</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Total Earnings</div>
                  <div className="text-xl font-bold">{formatCurrency(stats.weeklyEarnings)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Deliveries</div>
                  <div className="text-xl font-bold">{stats.totalDeliveries}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
